/**
 * <IndexPulse> — 3대 지수 stripe (S&P · NASDAQ · DOW)
 *
 * 미니 stripe 위젯 — 가격/변동률만 · AI 시황 정리는 별도 <MarketBrief> 위젯으로 분리.
 *
 * 데이터: GET /api/ticker/indices/explanation
 *   - indices[symbol] = { price, changePercent, ... }
 *   (같은 엔드포인트지만 marketBrief 는 MarketBrief 위젯에서 소비)
 */
"use client";

import { useEffect, useRef, useState } from "react";
import { TrendingDown, TrendingUp, Minus } from "lucide-react";
import type { WidgetMeta, WidgetProps } from "../types";

interface IndexItem {
  name: string;
  price: number | null;
  previousClose: number | null;
  changePercent: number | null;
  timeframe?: string | null;
  lastUpdated?: number | null;
  sparkline?: number[];
}

/** 미니 스파크라인 — 지수 col 내부 (mode 5/6 에서 CSS 로 노출) */
function MiniSpark({ values, up }: { values: number[]; up: boolean }) {
  if (!values || values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const W = 100;
  const H = 30;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * W;
    const y = H - ((v - min) / range) * H;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(" ");
  const areaPoints = `0,${H} ${pts} ${W},${H}`;
  const color = up ? "var(--bs-up)" : "var(--bs-down)";
  const gradId = `dw-ixp-spark-${up ? "up" : "down"}`;
  return (
    <svg className="dw-ixp-spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={areaPoints} fill={`url(#${gradId})`} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

interface ExplanationData {
  updatedAt: string;
  indices: Record<string, IndexItem>;
  // marketBrief 는 MarketBrief 위젯이 소비 — 이 위젯에선 무시
}

const INDEX_ORDER = [
  { symbol: "^GSPC", short: "S&P 500" },
  { symbol: "^IXIC", short: "NASDAQ" },
  { symbol: "^DJI", short: "DOW" },
];

const REFRESH_MS = 60 * 1000;

function formatPrice(v: number | null): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
}
function formatChange(v: number | null): string {
  if (v == null || !Number.isFinite(v)) return "";
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
}
function formatPct(pct: number | null): string {
  if (pct == null || !Number.isFinite(pct)) return "—";
  const sign = pct > 0 ? "+" : "";
  return `${sign}${(pct * 100).toFixed(2)}%`;
}

export function IndexPulse(_props: WidgetProps) {
  const [data, setData] = useState<ExplanationData | null>(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    async function load() {
      try {
        const res = await fetch("/api/ticker/indices/explanation", { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const d = (await res.json()) as ExplanationData;
        if (mountedRef.current) {
          setData(d);
          setLoading(false);
        }
      } catch {
        if (mountedRef.current) setLoading(false);
      }
    }
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(t);
    };
  }, []);

  return (
    <div className="dw-body dw-ixp">
      <div className="dw-head">
        <span className="dw-title">3대 지수</span>
      </div>

      {/* 3대 지수 가로 3분할 (stripe) */}
      <div className="dw-ixp-cols">
        {INDEX_ORDER.map((idx, i) => {
          const item = data?.indices?.[idx.symbol];
          const pct = item?.changePercent ?? null;
          const price = item?.price ?? null;
          const prev = item?.previousClose ?? null;
          const change = price != null && prev != null ? price - prev : null;
          const up = pct != null && pct > 0;
          const down = pct != null && pct < 0;
          const flat = pct != null && pct === 0;

          return (
            <div key={idx.symbol} className={`dw-ixp-col ${i > 0 ? "has-divider" : ""}`}>
              <div className="dw-ixp-label">{idx.short}</div>

              {/* 가격 (크게) */}
              <div className="dw-ixp-price-line">
                {loading && !item ? (
                  <span className="bs-skel" style={{ width: "80%", height: 24, display: "block" }} />
                ) : (
                  <span className="dw-ixp-price-big">{formatPrice(price)}</span>
                )}
              </div>

              {/* 변동률 (가격 아래 · 한 단계 큼) */}
              <div className={`dw-ixp-change ${up ? "up" : ""} ${down ? "down" : ""} ${flat ? "flat" : ""}`}>
                {up && <TrendingUp size={13} />}
                {down && <TrendingDown size={13} />}
                {flat && <Minus size={13} />}
                <span className="dw-ixp-change-pct">{formatPct(pct)}</span>
                {change != null && (
                  <span className="dw-ixp-change-abs">{formatChange(change)}</span>
                )}
              </div>

              {/* mini sparkline — CSS container query 가 mode 5/6 에서만 노출 */}
              {item?.sparkline && item.sparkline.length >= 2 && (
                <MiniSpark values={item.sparkline} up={up} />
              )}
            </div>
          );
        })}
      </div>

    </div>
  );
}

export const indexPulseMeta: WidgetMeta = {
  type: "index-pulse",
  name: "3대 지수",
  description: "S&P · NASDAQ · DOW — 크기에 따라 자동 배치",
  icon: "📈",
  category: "market",
  // 기본은 중간 크기 — CSS container query 가 크기별로 알아서 모드 전환
  defaultSize: { w: 6, h: 2 },
  availableSizes: [
    { w: 6, h: 1 },   // wide 1-row 압축 (라벨 + 가격 + %)
    { w: 12, h: 1 },  // full 1-row 압축
    { w: 6, h: 2 },   // wide 2-row + sparkline (default)
    { w: 12, h: 2 },  // full 2-row + sparkline
    { w: 3, h: 4 },   // narrow vertical
    { w: 4, h: 3 },   // narrow tall
    { w: 8, h: 3 },   // wide + tall
    { w: 12, h: 4 },  // full + tall (차트 공간 더 큼)
  ],
  minW: 3,
  maxW: 12,
  minH: 1,
  maxH: 6,
  component: IndexPulse,
};
